import React, {useState} from 'react'
import {Button} from 'react-daisyui'
import {useCancelReservationMutation} from '../../services/guestPortalApi'
import LoadingSpinner, {LoadingSpinnerSize} from '../common/LoadingSpinner'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faClose, faCheckCircle} from '@fortawesome/free-solid-svg-icons'
import {
  trackBookingManagement,
  trackFormSubmission
} from '../../services/analytics'
import {trackClick} from '../../services/coreAnalytics'
import {getUser, getUserToken} from '../../services/token'

interface CancelReservationFormProps {
  reservation: any
  onCancel?: () => void
  onGoBack?: () => void
  onFail?: () => void
}

const CancelReservationForm: React.FC<CancelReservationFormProps> = ({
  reservation,
  onCancel,
  onGoBack,
  onFail
}) => {
  const [
    cancelReservation,
    cancelReservationResult
  ] = useCancelReservationMutation()

  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [showSuccess, setShowSuccess] = useState<boolean>(false)

  return (
    <div
      aria-labelledby="cancel-reservation-title"
      onKeyDown={e => e.key === 'Escape' && onGoBack?.()}
    >
      {!showSuccess && (
        <span>
          <h1
            id="cancel-reservation-title"
            className="font-bold text-2xl mb-4"
            role="heading"
            aria-level={1}
          >
            Confirm Cancellation
          </h1>

          {cancelReservationResult.isLoading &&
            !cancelReservationResult.isError && (
              <div
                className="flex flex-row justify-center items-center pt-4 gap-2"
                role="status"
                aria-live="polite"
              >
                <LoadingSpinner size={LoadingSpinnerSize.Large} />
                <span>Loading...</span>
              </div>
            )}

          {cancelReservationResult.isError && (
            <div>
              <button
                className="btn-close"
                aria-label="Close cancellation policy"
                onClick={() => {
                  if (onGoBack) {
                    onGoBack()
                  }
                  setErrorMessage(null)
                }}
                onKeyDown={e => {
                  if (
                    e.key === 'Enter' ||
                    e.key === ' ' ||
                    e.key === 'Escape'
                  ) {
                    if (onGoBack) {
                      onGoBack()
                    }
                  }
                }}
              >
                <FontAwesomeIcon
                  icon={faClose}
                  className="text-xl fa-thin text-center"
                />
              </button>
              <div className="text-red-500" role="alert" aria-live="assertive">
                Unable to cancel reservation.
              </div>
            </div>
          )}
          {cancelReservationResult.isUninitialized && (
            <div>
              <div>
                <p>
                  Are you sure you want to cancel this reservation? This action
                  cannot be undone. If you need help or have any questions,
                  please reach out to our team.
                </p>
              </div>
              <div className="flex justify-end p-4">
                <Button
                  className="btn-md btn-outline btn-secondary mx-2"
                  onClick={() => {
                    // Track go back from cancellation
                    const token = getUserToken()
                    const user = token ? getUser(token) : null
                    trackClick(
                      'go-back-cancel-button',
                      `/member/stays/${reservation.reservationId}`,
                      user?.email
                    )
                    trackFormSubmission(
                      'booking_cancel',
                      'form_cancelled',
                      undefined,
                      user?.email
                    )

                    if (onGoBack) {
                      onGoBack()
                    }
                  }}
                  aria-label="Go back to the previous step"
                >
                  go back
                </Button>
                {!cancelReservationResult.error && (
                  <Button
                    className="btn btn-md btn-secondary"
                    onClick={() => {
                      // Track cancel reservation confirmation
                      const token = getUserToken()
                      const user = token ? getUser(token) : null
                      trackClick(
                        'confirm-cancel-reservation-button',
                        `/member/stays/${reservation.reservationId}`,
                        user?.email
                      )
                      trackFormSubmission(
                        'booking_cancel',
                        'form_submitted',
                        undefined,
                        user?.email
                      )

                      cancelReservation({
                        reservationId: reservation.reservationId,
                        dealId: reservation.dealId
                      })
                        .unwrap()
                        .then(() => {
                          // Track successful cancellation
                          trackBookingManagement(
                            'cancel_booking_completed',
                            reservation.reservationId,
                            undefined,
                            user?.email
                          )
                          trackFormSubmission(
                            'booking_cancel',
                            'form_success',
                            undefined,
                            user?.email
                          )

                          setShowSuccess(true)
                        })
                        .catch(() => {
                          // Track cancellation failure
                          trackBookingManagement(
                            'cancel_booking_cancelled',
                            reservation.reservationId,
                            'API Error',
                            user?.email
                          )
                          trackFormSubmission(
                            'booking_cancel',
                            'form_error',
                            ['Unable to cancel reservation'],
                            user?.email
                          )

                          setErrorMessage('Unable to cancel reservation')
                          if (onFail) {
                            onFail()
                          }
                        })
                    }}
                    aria-label="Confirm cancellation of the reservation"
                  >
                    cancel reservation
                  </Button>
                )}
              </div>
            </div>
          )}
        </span>
      )}
      {showSuccess && (
        <div className="">
          <div className="absolute top-0 right-0">
            <button
              className="btn-close"
              aria-label="Close cancellation policy"
              onClick={() => {
                if (onCancel) {
                  onCancel()
                }
                setErrorMessage(null)
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') {
                  if (onCancel) {
                    onCancel()
                  }
                }
              }}
            >
              <FontAwesomeIcon
                icon={faClose}
                className="text-xl fa-thin text-center"
              />
            </button>
          </div>
          <div className="text-green-500 flex flex-col items-center p-4">
            <FontAwesomeIcon icon={faCheckCircle} className="text-5xl p-4" />
          </div>
          <div className="text-center">
            Your reservation has been successfully cancelled. Still making a
            trip?{' '}
            <a
              href="https://www.placemakr.com/book-new"
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-cabana font-semibold"
            >
              Make a new reservation
            </a>
            .
          </div>
        </div>
      )}
    </div>
  )
}

export default CancelReservationForm
